/**
 * Stakeholders store (Sprint 4.3.8).
 *
 * Holds the in-memory stakeholder roster surfaced by the
 * StakeholderPicker + NewStakeholderDialog. Hydrates from the
 * `STAKEHOLDER_FIXTURES` seed on first read; user authoring (add /
 * edit / remove) lands in memory and persists to localStorage under
 * `cps:${userId}:stakeholders`.
 *
 * HC9 strict registry: every CRUD mutation emits a FHIR AuditEvent
 * (source `stakeholders-store-{add,edit,remove}`) plus a `pathway_edit`
 * telemetry envelope so the learning-loop dashboard surfaces the change.
 *
 * HC1 reproducibility: ids are stable slugs derived from the user-typed
 * name; never random. The store mirrors the Awell domain model file 03
 * stakeholder concept and the Phase B `stakeholder-model.md` Scrips role
 * tier extension.
 *
 * HC5: zero em / en dashes.
 *
 * Brand-Patch v2: zero `\bTLP[1-4]\b` user-facing. Stakeholder labels
 * are clinical-formal; tier badges resolve via `SCRIPS_ROLE_TIER_LABELS`.
 */
import { create } from 'zustand';
import { z } from 'zod';
import {
  stakeholderSchema,
  type Stakeholder,
} from '../types/stakeholder';
import { STAKEHOLDER_FIXTURES } from '../data/stakeholders';
import { emitTelemetry, newCorrelationId } from '../services/telemetry';
import { useUserStore } from './user.store';
import { useTenantStore } from './tenant.store';
import { useModeStore } from './mode.store';
import { auditPayloadToFhir } from '../services/fhir-mappers/audit-event';
import { getFhirClient } from '../services/fhir';

/** localStorage key per the SPEC tenant-scoped naming convention. */
export function stakeholdersStorageKey(userId: string): string {
  return `cps:${userId}:stakeholders`;
}

const stakeholderListSchema = z.array(stakeholderSchema);

function loadFromStorage(userId: string): Stakeholder[] | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(stakeholdersStorageKey(userId));
    if (raw === null) return null;
    const parsed = stakeholderListSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return null;
    return parsed.data as Stakeholder[];
  } catch {
    return null;
  }
}

function persistToStorage(
  userId: string,
  stakeholders: ReadonlyArray<Stakeholder>,
): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(
    stakeholdersStorageKey(userId),
    JSON.stringify(stakeholders),
  );
}

const STAKEHOLDER_PERSIST_ERROR_MESSAGE =
  'Stakeholder roster could not be persisted. Try again.';

interface AuditArgs {
  verb: 'added' | 'edited' | 'removed';
  source:
    | 'stakeholders-store-add'
    | 'stakeholders-store-edit'
    | 'stakeholders-store-remove';
  stakeholder: Stakeholder;
}

function emitStakeholderAudit({ verb, source, stakeholder }: AuditArgs): void {
  const tenantId = useTenantStore.getState().tenantId;
  const mode = useModeStore.getState().mode;
  const correlationId = newCorrelationId();
  const recorded = new Date().toISOString();

  // pathway_edit envelope for the learning-loop dashboard. Carries
  // mode + correlation id so the audit lines up with other edits.
  emitTelemetry('pathway_edit', {
    pathway_id: `stakeholder:${stakeholder.id}`,
    node_id: stakeholder.id,
    edit_type: 'node_property',
    before: verb === 'added' ? null : stakeholder,
    after: verb === 'removed' ? null : stakeholder,
    mode,
    pathway_edit_live: mode === 'live',
    correlation_id: correlationId,
    ts: recorded,
  });

  // FHIR AuditEvent emission. Fire-and-forget; failure is logged but
  // does not block the store mutation per the addNode pattern in
  // `pathway.store.ts`.
  try {
    const audit = auditPayloadToFhir({
      recorded,
      source,
      tenantId,
      subject: {
        reference: 'Practitioner/pathway-author',
        display: 'Pathway author',
      },
      verb,
      verbDisplay: `${verb.charAt(0).toUpperCase()}${verb.slice(1)} stakeholder ${stakeholder.name}`,
      object: {
        reference: `urn:cps:stakeholder:${stakeholder.id}`,
        display: stakeholder.name,
        typeCode: 'stakeholder',
      },
      action: verb === 'added' ? 'C' : verb === 'edited' ? 'U' : 'D',
      outcome: '0',
    });
    void getFhirClient()
      .createAuditEvent(audit, tenantId)
      .catch((err) => {
        console.warn(
          '[stakeholders.store] AuditEvent emit failed',
          err,
        );
      });
  } catch (err) {
    console.warn('[stakeholders.store] audit assembly failed', err);
  }
}

export interface StakeholdersStoreState {
  stakeholders: Stakeholder[];
  hydrated: boolean;
  persistError: string | null;
  hydrate: () => Stakeholder[];
  addStakeholder: (
    stakeholder: Stakeholder,
  ) => { ok: true; stakeholder: Stakeholder } | { ok: false; error: string };
  editStakeholder: (
    id: string,
    patch: Partial<Omit<Stakeholder, 'id'>>,
  ) => Stakeholder | null;
  removeStakeholder: (id: string) => boolean;
  reset: () => void;
}

/**
 * Reset helper for tests. Clears the in-memory state + persist error.
 * Production paths should never call this.
 */
function freshState(): Pick<
  StakeholdersStoreState,
  'stakeholders' | 'hydrated' | 'persistError'
> {
  return { stakeholders: [], hydrated: false, persistError: null };
}

export const useStakeholdersStore = create<StakeholdersStoreState>(
  (set, get) => ({
    ...freshState(),
    hydrate: () => {
      if (get().hydrated) return get().stakeholders;
      const userId = useUserStore.getState().userId;
      const persisted = loadFromStorage(userId);
      const seed = persisted ?? STAKEHOLDER_FIXTURES.map((s) => ({ ...s }));
      set({ stakeholders: seed as Stakeholder[], hydrated: true });
      return get().stakeholders;
    },
    addStakeholder: (stakeholder) => {
      const parsed = stakeholderSchema.safeParse(stakeholder);
      if (!parsed.success) {
        return {
          ok: false,
          error:
            parsed.error.errors[0]?.message ?? 'Invalid stakeholder shape.',
        };
      }
      const validated = parsed.data as Stakeholder;
      const previous = get().hydrated
        ? get().stakeholders
        : get().hydrate();
      if (previous.some((s) => s.id === validated.id)) {
        return {
          ok: false,
          error: `A stakeholder with id "${validated.id}" already exists.`,
        };
      }
      const next = [...previous, validated];
      set({ stakeholders: next });
      try {
        const userId = useUserStore.getState().userId;
        persistToStorage(userId, next);
      } catch (err) {
        console.warn(
          '[stakeholders.store] persist failed; reverting addStakeholder',
          err,
        );
        set({
          stakeholders: previous,
          persistError: STAKEHOLDER_PERSIST_ERROR_MESSAGE,
        });
        return { ok: false, error: STAKEHOLDER_PERSIST_ERROR_MESSAGE };
      }
      if (get().persistError !== null) set({ persistError: null });
      emitStakeholderAudit({
        verb: 'added',
        source: 'stakeholders-store-add',
        stakeholder: validated,
      });
      return { ok: true, stakeholder: validated };
    },
    editStakeholder: (id, patch) => {
      const previous = get().hydrated
        ? get().stakeholders
        : get().hydrate();
      const index = previous.findIndex((s) => s.id === id);
      if (index < 0) return null;
      const merged = { ...previous[index], ...patch, id };
      const parsed = stakeholderSchema.safeParse(merged);
      if (!parsed.success) return null;
      const validated = parsed.data as Stakeholder;
      const next = [...previous];
      next[index] = validated;
      set({ stakeholders: next });
      try {
        const userId = useUserStore.getState().userId;
        persistToStorage(userId, next);
      } catch (err) {
        console.warn(
          '[stakeholders.store] persist failed; reverting editStakeholder',
          err,
        );
        set({
          stakeholders: previous,
          persistError: STAKEHOLDER_PERSIST_ERROR_MESSAGE,
        });
        return null;
      }
      if (get().persistError !== null) set({ persistError: null });
      emitStakeholderAudit({
        verb: 'edited',
        source: 'stakeholders-store-edit',
        stakeholder: validated,
      });
      return validated;
    },
    removeStakeholder: (id) => {
      const previous = get().hydrated
        ? get().stakeholders
        : get().hydrate();
      const target = previous.find((s) => s.id === id);
      if (!target) return false;
      const next = previous.filter((s) => s.id !== id);
      set({ stakeholders: next });
      try {
        const userId = useUserStore.getState().userId;
        persistToStorage(userId, next);
      } catch (err) {
        console.warn(
          '[stakeholders.store] persist failed; reverting removeStakeholder',
          err,
        );
        set({
          stakeholders: previous,
          persistError: STAKEHOLDER_PERSIST_ERROR_MESSAGE,
        });
        return false;
      }
      if (get().persistError !== null) set({ persistError: null });
      emitStakeholderAudit({
        verb: 'removed',
        source: 'stakeholders-store-remove',
        stakeholder: target,
      });
      return true;
    },
    reset: () => {
      set({ ...freshState() });
    },
  }),
);

/**
 * Pure helper for separation-of-duty checks. Given the active assignee
 * roster (existing assignment role -> stakeholder), determine whether
 * a candidate stakeholder may be assigned without violating Phase B
 * `stakeholder-model.md` separation-of-duty rules.
 *
 * Returns `null` if the assignment is permitted, or a structured warning
 * payload identifying the conflicting incumbent stakeholder + role.
 */
export interface SeparationConflict {
  candidate: Stakeholder;
  incumbent: Stakeholder;
}

export function detectSeparationConflict(
  candidate: Stakeholder,
  existing: ReadonlyArray<Stakeholder>,
): SeparationConflict | null {
  for (const incumbent of existing) {
    if (incumbent.id === candidate.id) continue;
    if ((candidate.incompatibleWith ?? []).includes(incumbent.role)) {
      return { candidate, incumbent };
    }
    if ((incumbent.incompatibleWith ?? []).includes(candidate.role)) {
      return { candidate, incumbent };
    }
  }
  return null;
}
